import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { api, ApiError } from '../lib/api';
import { Icon } from './Icon';

/**
 * "💡 Suggest a change", on every page: the idea goes to the administrator (notification and email) with the page it
 * came from; the person is told the answer. The list is at /suggestions.
 */
export function SuggestButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loc = useLocation();
  const page = typeof document !== 'undefined' ? `${document.title} · ${loc.pathname}` : loc.pathname;
  const send = useMutation({
    mutationFn: () => api('/api/suggestions', { method: 'POST', body: { text, page } }),
    onSuccess: () => { setSent(true); setText(''); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send.'),
  });
  const close = () => { setOpen(false); setSent(false); setError(null); };
  return (
    <>
      <button className="icon-btn" aria-label="Suggest a change" title="Suggest a change to this page" onClick={() => setOpen(true)}>
        <span aria-hidden="true" style={{ fontSize: 18 }}><Icon name="bulb" /></span>
      </button>
      {open && (
        <div className="modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="modal popup" role="dialog" aria-modal="true" aria-labelledby="suggest-title">
            <h2 id="suggest-title"><Icon name="bulb" />Suggest a change</h2>
            {sent ? (
              <>
                <p>Thank you! It was sent to the administrator. You’ll be told the answer.</p>
                <div className="actions popup-actions">
                  <Link className="btn" to="/suggestions" onClick={close}>My suggestions</Link>
                  <button className="btn primary" onClick={close}>Close</button>
                </div>
              </>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
                <p className="small muted">What would make this page better for you? A field, a button, a list, a report… The page you are on is sent with it.</p>
                <label className="field"><span className="label">Your idea</span>
                  <textarea rows={5} required minLength={3} maxLength={4000} autoFocus dir="auto" value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="e.g. In My Desk, show the consultant’s name next to each deal." />
                </label>
                {error && <p className="form-error">{error}</p>}
                <div className="actions popup-actions">
                  <Link className="btn ghost" to="/suggestions" onClick={close}>My suggestions</Link>
                  <button type="button" className="btn ghost" onClick={close}>Cancel</button>
                  <button className="btn primary" disabled={send.isPending || text.trim().length < 3}>{send.isPending ? 'Sending…' : 'Send'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
